<template>
    <Modal id="hold-cart-modal"
           data-component="HoldCartModal"
           :icon="icon"
           :title="title"
           ok-label="ORDERS_ON_HOLD.HOLD_BUTTON"
           :cancel="cancel">
        <template v-slot:default>
            <p v-if="message"
               class="alert alert-info text-left alert--small">
                <i class="fa-solid fa-circle-exclamation"></i> {{ l(message) }}
            </p>

            <p v-if="!onlyAllowAssignStaffRef && stashedCart.currentCart.containsKitchenItems()"
               class="alert alert-info text-left alert--small">
                <i class="fa-solid fa-circle-exclamation"></i> {{ l('ORDERS_ON_HOLD.KITCHEN_INFO') }}
            </p>

            <template v-if="stashedCart">
                <CartOverview v-if="stashedCart"
                              ref="stash"
                              :edit-ref="true"
                              :stashed-cart="stashedCart"
                              :staff-ref-placeholder="staffRefPlaceholderValue"
                              :allow-table-view="referenceIsRequired"
                              @view-tables="viewTables"
                              @enter-key-down="enterAction"></CartOverview>
            </template>
        </template>

        <template v-slot:footer>
            <div class="modl-footer">
                <SquareBtn size="lg"
                           class="cancel-btn "
                           @click="cancel">
                    {{ l('CANCEL') }}
                </SquareBtn>
                <SquareBtn size="lg"
                           :disabled="referenceIsRequired && !refIsValid"
                           :loading="settingRef"
                           @click="setReference">
                    {{ l(referenceButtonTextValue) }}
                </SquareBtn>
                <SquareBtn v-if="!onlyAllowAssignStaffRef"
                           color="primary"
                           size="lg"
                           :loading="holding"
                           @click="hold">
                    {{ l('ORDERS_ON_HOLD.HOLD_BUTTON') }}
                </SquareBtn>
            </div>
        </template>
    </Modal>
</template>

<script>
import CartOverview from '@/epos/vue/modals/common/CartOverview';
import Modal from '@/vue/components/Modals/Modal';
import SquareBtn from '@/vue/components/SquareBtn';
import { container } from '@/container';
import { mergeStashedCarts } from '@/lib/StashedCarts/mergeStashedCarts';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

export default {
    name: 'HoldCartModal',

    components: {
        Modal,
        SquareBtn,
        CartOverview,
    },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        // If we click the Hold button we want to show the Hold and Assign Reference Only buttons but if the modal is
        // launched due to validation requiring a staffRef when clicking pay we only want to capture the staffRef.
        onlyAllowAssignStaffRef: {
            type: Boolean,
            default: false,
        },
        icon: {
            type: String,
            default: 'fa-cloud-arrow-up',
        },
        message: {
            type: String,
            default: null,
        },
        title: {
            type: String,
            default: 'ORDERS_ON_HOLD.HOLD_MODAL_TITLE',
        },
    },

    data() {
        return {
            holding: false,
            settingRef: false,
            stashedCart: null,
        };
    },

    computed: {
        referenceIsRequired() {
            return this.onlyAllowAssignStaffRef;
        },
        referenceButtonTextValue() {
            if (this.onlyAllowAssignStaffRef) {
                return 'ORDERS_ON_HOLD.SET_REFERENCE_BTN_ONLY_REF';
            }
            return 'ORDERS_ON_HOLD.SET_REFERENCE_BUTTON';
        },
        staffRefPlaceholderValue() {
            if (this.referenceIsRequired) {
                return 'ORDERS_ON_HOLD.STAFF_REF_REQUIRED_PLACEHOLDER';
            }
            return 'ORDERS_ON_HOLD.STAFF_REF_OPTIONAL_PLACEHOLDER';
        },
        refIsValid() {
            return !!this.stashedCart.currentCart.staffRef;
        },
    },

    created() {
        this.stashedCart = container.cartStashHelperService.cartToStash(container.cartService);
    },

    mounted() {
        this.$nextTick(() => {
            // Focus on the reference input on load.
            if (this.$refs.stash && this.$refs.stash.$refs['ref-input']) {
                this.$refs.stash.$refs['ref-input'].focus();
            }
        });
    },

    methods: {
        setReference() {
            this.settingRef = true;

            // A ref isn't mandatory when setting the ref, because you might want to
            // set it back to blank.
            const newRef = this.stashedCart.currentCart.staffRef;
            return container.staffRefValidator
                .setCartStaffRef(container.cartService.currentCart, newRef)
                .then(() => {
                    // Done.
                    this.resolve({
                        refSet: true,
                        held: false,
                    });
                })
                .finally(() => {
                    this.settingRef = false;
                });
        },

        // When the user has typed a reference and presses return/enter instead of clicking a button
        // what should the action be?
        enterAction() {
            if (this.onlyAllowAssignStaffRef) {
                this.setReference();
                return;
            }
            this.hold();
        },

        hold() {
            this.holding = true;

            const newRef = this.stashedCart.currentCart.staffRef;

            let checkForDuplicatePromise;
            if (this.stashedCart.currentCart.ignoreDuplicateRef) {
                // User has previously selected to ignore a duplicate ref / not to merge, so honour that choice.
                checkForDuplicatePromise = Promise.resolve(this.stashedCart);
            } else {
                checkForDuplicatePromise = container.staffRefValidator.findExistingStashWithRef(newRef)
                    .then((existingStashWithRef) => {
                        if (existingStashWithRef) {
                            // There is another stash with the same ref.
                            // Ask if they want to merge this new stash with that one.
                            return container.vueModalFactory
                                .open(
                                    MODAL_NAMES__EPOS.MERGE_CART,
                                    {
                                        isKitchenOrderMerge: false,
                                        newStashedCart: this.stashedCart,
                                        existingStashedCart: existingStashWithRef,
                                    },
                                )
                                .then((shouldMerge) => {
                                    if (shouldMerge) {
                                        return mergeStashedCarts(existingStashWithRef, this.stashedCart);
                                    }

                                    // Remember on the cart that the user chose to ignore the duplicate ref.
                                    this.stashedCart.currentCart.ignoreDuplicateRef = true;

                                    // Save the new stash as-is.
                                    return this.stashedCart;
                                });
                        }

                        // If there is no other stash with the same ref we don't need to do anything
                        // and can go ahead and save the stash as-is.
                        return this.stashedCart;
                    });
            }

            checkForDuplicatePromise.then((stashedCartToSave) => {
                // Save the new stash.
                // This might be the current cart contents or it might the current cart contents
                // merged with another stash.
                container.cartStasher.saveStashedCart(stashedCartToSave);

                // Done.
                this.resolve({
                    held: true,
                });
            }).catch(() => {
                this.holding = false;
            }).finally(() => {
                this.holding = false;
            });
        },

        viewTables() {
            container.vueModalFactory.open(MODAL_NAMES__EPOS.TABLE_PICKER, { resolveOnly: true })
                .then((res) => {
                    if (res && res.value) {
                        this.stashedCart.currentCart.staffRef = res.value;
                    }
                });
        },
    },
};
</script>
